import React, { useState } from 'react';
import { getUser, User } from '../api/users';
import { getSpace, Space } from '../api/spaces';
import { getChannels, Channel } from '../api/channels';
import { FeedbackPage, getFeedbackPage } from '../api/feedbackPage';
import { getTiles, Tile } from '../api/tiles';
import { getOrders, TileOrder } from '../api/orders';
import { FeedbackResponse, getAllFeedback } from '../api/feedback';

interface LoadedValue<T> {
    loading: boolean
    loaded: boolean
    value: T | null
    load: (reload?: boolean) => Promise<void>
    setValue: (value: T) => void
    clear: () => void
}

const createLoadedValue = <T extends {}>(loader: () => Promise<T | undefined | null>, onChange?: () => void): LoadedValue<T> => {
    const [value, setValue] = useState<T | null>(null)
    const [loaded, setLoaded] = useState(false)
    const [loading, setLoading] = useState(false)

    const load = async (reload?: boolean) => {
        if (value && !reload) {
            return
        }

        setLoading(true)
        const newValue = await loader()
        setValue(newValue || null)
        setLoading(false)
        setLoaded(true)
        onChange?.()
    }

    const clear = () => {
        setValue(null)
        setLoaded(false)
        onChange?.()
    }

    return {
        loading,
        loaded,
        value,
        load,
        setValue,
        clear
    }
}

export interface AppContextValue {
    user: LoadedValue<User>
    space: LoadedValue<Space>
    channels: LoadedValue<Channel[]>
    tiles: LoadedValue<Tile[]>
    feedbackPage: LoadedValue<FeedbackPage>
    orders: LoadedValue<TileOrder[]>
    feedback: LoadedValue<FeedbackResponse[]>
}

export const AppContext = React.createContext<AppContextValue>(null as any)

export const useNewAppContext = (): AppContextValue => {
    const space = createLoadedValue<Space>(getSpace)
    const channels = createLoadedValue<Channel[]>(getChannels)
    const tiles = createLoadedValue<Tile[]>(getTiles)
    const feedbackPage = createLoadedValue<FeedbackPage>(getFeedbackPage)
    const orders = createLoadedValue<TileOrder[]>(getOrders)
    const feedback = createLoadedValue<FeedbackResponse[]>(getAllFeedback)

    const clearAll = () => {
        space.clear()
        channels.clear()
        feedbackPage.clear()
        tiles.clear()
        orders.clear()
        feedback.clear()
    }

    return {
        user: createLoadedValue<User>(getUser, clearAll),
        space,
        channels,
        tiles,
        feedbackPage,
        orders,
        feedback
    }
}

export const useAppContext = (): AppContextValue => {
    return React.useContext(AppContext)
}
